<script setup lang="ts">
import { computed, h, onMounted, ref, type VNode } from 'vue';
import { useRouter } from 'vue-router';
import type { DataTableColumns } from 'naive-ui';
import type { ContractTypeInDb } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaEditButton from '@/components/design/base/GaiaEditButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaTooltipProps } from '@/components/design/base/GaiaTooltip.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaContractTypeDeleteModal from '@/components/design/domain/contractTypes/GaiaContractTypeDeleteModal.vue';
import GaiaContractTypeFilter from '@/components/design/domain/contractTypes/GaiaContractTypeFilter.vue';
import GaiaContractTypeFormModal from '@/components/design/domain/contractTypes/GaiaContractTypeFormModal.vue';
import { useAuthStore } from '@/stores/auth';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { contractTypeRoute } from '@/router/links';

/**
 * The organisation's contract types: what contracts are classified as, and
 * what playbooks and clauses are scoped to. Everyone can read the list; only
 * an org admin can change it.
 */
const router = useRouter();
const authStore = useAuthStore();
const contractTypeStore = useContractTypeStore();

const canWrite = computed(() => authStore.userPermissions.is_org_admin);

// The list is the org's whole set, so search narrows it here rather than on the server.
const search = ref('');
/** The contract types the filter narrows the list to; empty shows them all. */
const pickedTypeUids = ref<string[]>([]);
const visibleTypes = computed(() => {
  const text = search.value.trim().toLowerCase();
  const picked = new Set(pickedTypeUids.value);
  return contractTypeStore.sortedContractTypes.filter(
    type =>
      (picked.size === 0 || picked.has(type.uid)) &&
      (!text ||
        type.name.toLowerCase().includes(text) ||
        type.description.toLowerCase().includes(text))
  );
});

const showEmpty = computed(
  () => contractTypeStore.loaded && contractTypeStore.contractTypes.length === 0
);

const formOpen = ref(false);
const deleteOpen = ref(false);
const activeType = ref<ContractTypeInDb | null>(null);

function openCreate(): void {
  activeType.value = null;
  formOpen.value = true;
}

function onRowAction(type: ContractTypeInDb, key: string): void {
  activeType.value = type;
  if (key === 'edit') formOpen.value = true;
  if (key === 'delete') deleteOpen.value = true;
}

function renderRowActions(type: ContractTypeInDb): VNode {
  // Their own clicks must not also open the row.
  return h(
    'div',
    {
      style: { display: 'flex', justifyContent: 'flex-end', gap: '2px' },
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
      },
    },
    [
      h(GaiaEditButton, {
        size: 'md',
        subject: 'contract type',
        onClick: () => {
          onRowAction(type, 'edit');
        },
      }),
      h(GaiaDeleteButton, {
        size: 'md',
        subject: 'contract type',
        onClick: () => {
          onRowAction(type, 'delete');
        },
      }),
    ]
  );
}

/** Rows open the type's page. */
function rowProps(type: ContractTypeInDb): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(contractTypeRoute(type.uid));
    },
  };
}

const columns = computed((): DataTableColumns<ContractTypeInDb> => [
  {
    title: 'Name',
    key: 'name',
    width: 260,
    render: row =>
      h(
        GaiaText,
        { size: 'body', weight: 'semibold', tone: 'ink', truncate: true },
        () => row.name
      ),
  },
  {
    title: 'Description',
    key: 'description',
    ellipsis: { tooltip: gaiaTooltipProps },
    render: row => h(GaiaText, { size: 'control', tone: 'subtle' }, () => row.description),
  },
  {
    title: 'Prompts',
    key: 'extraction_prompt_count',
    width: 110,
    align: 'right',
    render: row =>
      h(GaiaText, { size: 'control', tone: 'subtle' }, () => row.extraction_prompt_count ?? '–'),
  },
  ...(canWrite.value
    ? [
        {
          title: '',
          key: 'actions',
          width: 80,
          align: 'right' as const,
          render: renderRowActions,
        },
      ]
    : []),
]);

onMounted(() => {
  // Refetched, not ensureLoaded: the prompt counts change as prompts are added and removed.
  void contractTypeStore.fetchContractTypes();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.contractTypes }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="18">
      <n-flex align="flex-start" :size="[24, 12]">
        <n-flex vertical :size="6" :style="{ flex: '1 1 320px', minWidth: 0 }">
          <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
            {{ navLabels.contractTypes }}
          </gaia-header>
        </n-flex>
        <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="search"
            placeholder="Search contract types"
            :debounce="150"
            style="width: 220px"
          />
          <gaia-contract-type-filter v-model="pickedTypeUids" />
          <gaia-create-button v-if="canWrite" subject="contract type" @click="openCreate" />
        </n-flex>
      </n-flex>

      <gaia-empty-state v-if="showEmpty" dashed title="No contract types yet">
        Create the types your organisation's contracts should be classified as.
        <template v-if="canWrite" #actions>
          <gaia-create-button subject="contract type" :icon="false" @click="openCreate" />
        </template>
      </gaia-empty-state>

      <gaia-table
        v-else
        :columns="columns"
        :data="visibleTypes"
        :loading="contractTypeStore.loading"
        :row-key="(row: ContractTypeInDb) => row.uid"
        :row-props="rowProps"
      >
        <template #empty>
          <n-empty
            v-if="!contractTypeStore.loading"
            :description="
              search.trim() ? `No contract type matches “${search.trim()}”` : 'No contract types'
            "
          />
        </template>
      </gaia-table>
    </n-flex>

    <gaia-contract-type-form-modal v-model:show="formOpen" :contract-type="activeType" />
    <gaia-contract-type-delete-modal v-model:show="deleteOpen" :contract-type="activeType" />
  </Layout>
</template>
